import FFPaidTransfersAlpha from '@wetransfer/transfer/constant/flags/paid-transfers-alpha';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import { CURRENCIES, UserState } from '@wetransfer/transfer/store/types';
import { TRANSFER_TYPES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-types';
import { NormalizedTransfer } from '@wetransfer/transfer/transfer-bundle/types/transfers';

import { userHasPremium2022Tier } from './user';

/**
 * Determines if a transfer is paid and if the Paid Transfers buyer experiment is enabled
 * @param isPaid the `paid` property of a transfer object
 * @returns boolean
 */
export function isPaidTransfer(isPaid?: boolean): boolean {
  return Boolean(isPaid);
}

export function canUserCreatePaidTransfer(user: UserState): boolean {
  return (
    FFPaidTransfersAlpha.isEnablePaidTransfers() && userHasPremium2022Tier(user)
  );
}

export function canSellTransfer(
  transferType: TRANSFER_TYPES,
  isPremiumUser: boolean
): boolean {
  return transferType === 'link' && isPremiumUser;
}

/**
 * Converts currency value to cents
 * @param value the value of currency
 * @returns number
 */
export const convertToCents = (value: number): number => value * 100;

/**
 * Converts cents to a human readable currency value
 * @param value the value in cents
 * @returns number
 */
export const convertFromCents = (value: number): number => value / 100;

export const getTransferPriceAsFloat = (
  transfer: unknown
): number | undefined => {
  const price = (transfer as NormalizedTransfer).price?.amount;

  return typeof price === 'number' ? convertFromCents(price) : undefined;
};

export const formatTransferPrice = (
  price: number,
  currency: CURRENCIES
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

export const MIN_VALUE_ACCEPTED = 100; // cents

export const MAX_VALUE_ALLOWED = 99900; // cents

export const POLL_ORDER_STATUS_INTERVAL = 2500; // ms

const getParsedAccessTokensByTransfer = () => {
  if (!localStorage) {
    return {};
  }

  try {
    return JSON.parse(
      localStorage.getItem(
        AppConfig.localStorage.currentOrderAccessToken.name
      ) || '{}'
    );
  } catch (error) {
    // The local storage item was used for storing a single access token.
    // A change was made here (https://github.com/WeTransfer/frontend/pull/18265)
    // to support access tokens for multiple orders by transfer id, this block catches
    // the error in the case where `JSON.parse` will fail in the try block as
    // the value would've been a string and not a JSON
    return {};
  }
};

export const getCurrentOrderAccessToken = (
  transferId: string
): string | undefined => {
  return getParsedAccessTokensByTransfer()[transferId];
};

export const setCurrentOrderAccessToken = (
  transferId: string,
  value: string
): boolean => {
  const parsedAccessTokens = getParsedAccessTokensByTransfer();
  parsedAccessTokens[transferId] = value;

  if (localStorage) {
    localStorage.setItem(
      AppConfig.localStorage.currentOrderAccessToken.name,
      JSON.stringify(parsedAccessTokens)
    );

    return true;
  }

  return false;
};

export const unsetCurrentOrderAccessToken = (transferId: string): boolean => {
  const parsedAccessTokens = getParsedAccessTokensByTransfer();
  delete parsedAccessTokens[transferId];

  if (localStorage) {
    localStorage.setItem(
      AppConfig.localStorage.currentOrderAccessToken.name,
      JSON.stringify(parsedAccessTokens)
    );

    return true;
  }

  return false;
};

export const getVerifyToken = (): string | undefined =>
  new URLSearchParams(window.location.search).get('verify_token');
